import { NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, inject, input } from '@angular/core';
import { LoaderComponent } from '../loader/loader.component';

/**
 * @description
 * A loading overlay component that displays a rotating logo and skeleton UI for data loading states.
 * The component shows an animated logo in the center of the screen with customizable loading text,
 * along with a configurable skeleton grid that mimics the layout of a data table or spreadsheet.
 *
 * @example
 * Basic usage:
 * ```html
 * <unified-ui-grid-loading-overlay-skeleton></unified-ui-grid-loading-overlay-skeleton>
 * ```
 *
 * Fixed number of rows:
 * ```html
 * <unified-ui-grid-loading-overlay-skeleton
 *   [loadingText]="'Processing data...'"
 *   [showSkeleton]="true"
 *   [numberOfColumns]="8"
 *   [numberOfRows]="15"
 *   [showRowHeader]="true"
 *   [minColumnWidth]="'100px'"
 *   [rotationSpeed]="'2s'">
 * </unified-ui-grid-loading-overlay-skeleton>
 * ```
 *
 * Dynamic rows that fill the container:
 * ```html
 * <unified-ui-grid-loading-overlay-skeleton
 *   [loadingText]="'Processing data...'"
 *   [showSkeleton]="true"
 *   [fillContainer]="true"
 *   [rowHeight]="28"
 *   [numberOfColumns]="8"
 *   [showRowHeader]="true"
 *   [minColumnWidth]="'100px'"
 *   [rotationSpeed]="'2s'">
 * </unified-ui-grid-loading-overlay-skeleton>
 * ```
 *
 * @usageNotes
 * The component is designed to be used as a full-screen overlay within a container that has
 * position: relative. It will automatically center the loading indicator and adjust the
 * skeleton grid to the available space.
 *
 * The skeleton grid is responsive and will:
 * - Adjust columns evenly across available space
 * - Maintain minimum column width
 * - Enable horizontal scrolling if needed
 * - Show scrollbars only when content overflows
 * - Keep header and footer visible during scroll
 *
 * When using the `fillContainer` property, the component will:
 * - Dynamically calculate the appropriate number of rows based on container height
 * - Adjust the number of rows when the container is resized
 * - Ensure a minimum number of rows (10) for smaller containers
 * - Use the `rowHeight` property to determine how many rows can fit in the available space
 */
@Component({
    selector: 'unified-ui-grid-loading-overlay-skeleton',
    imports: [NgClass, LoaderComponent],
    standalone: true,
    templateUrl: './grid-loading-overlay-skeleton.component.html',
    styleUrl: './grid-loading-overlay-skeleton.component.scss'
})
export class GridLoadingOverlaySkeletonComponent implements OnInit, AfterViewInit, OnDestroy {
    private _elementRef = inject(ElementRef);

    /**
     * The text to display below the loading spinner.
     * @default 'Loading data...'
     */
    public readonly loadingText = input('Loading data...');

    /**
     * Whether to show the skeleton grid UI behind the loader.
     * @default true
     */
    public readonly showSkeleton = input(true);

    /**
     * The number of columns to show in the skeleton grid.
     * @default 6
     */
    public readonly numberOfColumns = input(6);

    /**
     * The number of rows to show in the skeleton grid.
     * Only used when fillContainer is false.
     * @default 20
     */
    public readonly numberOfRows = input(20);

    /**
     * Whether to dynamically calculate the number of rows based on container height.
     * When true, numberOfRows is ignored and rows are calculated to fill the container.
     * This provides a more responsive layout that adapts to different screen sizes.
     * @default false
     */
    public readonly fillContainer = input(false);

    /**
     * The height of each row in pixels, used for calculating the number of rows when fillContainer is true.
     * Increasing this value will result in fewer, taller rows, while decreasing it will create more, shorter rows.
     * Only used when fillContainer is true.
     * @default 28
     */
    public readonly rowHeight = input(28);

    /**
     * Whether to show the title row at the top of the skeleton grid.
     * @default true
     */
    public readonly showTitle = input(true);
    /**
     * Whether to show the row header column in the skeleton grid.
     * @default true
     */
    public readonly showRowHeader = input(true);

    /**
     * Whether to show the footer row at the bottom of the skeleton grid.
     * @default true
     */
    public readonly showFooter = input(true);

    /**
     * The minimum width of each column in the skeleton grid.
     * Accepts any valid CSS width value.
     * @default '100px'
     */
    public readonly minColumnWidth = input('100px');

    /**
     * The speed of the logo rotation animation.
     * Accepts any valid CSS time value.
     * @default '2s'
     */
    public readonly rotationSpeed = input('2s');

    /**
     * Generates an array of column indices for the skeleton grid.
     * @returns An array of numbers from 1 to numberOfColumns
     */
    public get columns(): number[] {
        return this._generateArray(this.numberOfColumns());
    }

    /**
     * The actual number of rows to display, either fixed or dynamically calculated.
     */
    private _calculatedRows = 20;

    /**
     * ResizeObserver to detect container size changes.
     */
    private _resizeObserver: ResizeObserver | null = null;

    public ngOnInit(): void {
        this._calculatedRows = this.numberOfRows();
        if (this.fillContainer()) {
            this._calculateRowsBasedOnHeight();
        }
    }

    public ngAfterViewInit(): void {
        if (this.fillContainer()) {
            // Initial calculation with a slight delay to ensure container is rendered
            setTimeout(() => {
                this._calculateRowsBasedOnHeight();
            }, 0);

            // Set up resize observer if available
            if (typeof ResizeObserver !== 'undefined') {
                this._resizeObserver = new ResizeObserver(() => {
                    this._calculateRowsBasedOnHeight();
                });
                this._resizeObserver.observe(this._elementRef.nativeElement);
            }
        }
    }

    public ngOnDestroy(): void {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }

    @HostListener('window:resize')
    public onResize(): void {
        if (this.fillContainer()) {
            this._calculateRowsBasedOnHeight();
        }
    }

    /**
     * Calculates the number of rows based on the container height.
     */
    private _calculateRowsBasedOnHeight(): void {
        if (!this.fillContainer()) return;

        const containerElement = this._elementRef.nativeElement;
        if (!containerElement) return;

        // Get container height
        const containerHeight = containerElement.clientHeight || window.innerHeight;

        // Account for header and footer heights (approximately)
        const headerHeight = 60; // Title + header row
        const footerHeight = 40; // Footer row

        // Ensure we have a minimum available height
        const availableHeight = Math.max(300, containerHeight - headerHeight - footerHeight);

        // Calculate rows based on row height (including gap)
        const rowHeightWithGap = this.rowHeight() + 8; // 8px for gap

        // Ensure we have at least 10 rows or enough to fill the container
        const calculatedRows = Math.max(10, Math.floor(availableHeight / rowHeightWithGap));

        // Update calculated rows
        this._calculatedRows = calculatedRows;

        // Force change detection by logging (for debugging)
        // console.debug(`Container height: ${containerHeight}px, Calculated rows: ${calculatedRows}`);
    }

    /**
     * Generates an array of row indices for the skeleton grid.
     * @returns An array of numbers from 1 to calculatedRows
     */
    public get rows(): number[] {
        return this._generateArray(this._calculatedRows);
    }

    /**
     * Generates the CSS animation style for the logo rotation.
     * @returns A CSS animation string
     */
    public get rotationStyle(): string {
        return `rotate ${this.rotationSpeed()} linear infinite`;
    }

    /**
     * Helper method to generate numbered arrays for rows and columns.
     * @param length The length of the array to generate
     * @returns An array of numbers from 1 to length
     * @private
     */
    private _generateArray(length: number): number[] {
        return Array(length)
            .fill(0)
            .map((_, i) => i + 1);
    }
}
